import { useId, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { ArrowLeft, ArrowUpRight, Bot, Check, ChevronRight, LoaderCircle, Users } from "lucide-react";
import { formatMoney, formatTokenQuantity } from "./format";
import type { AuthorityView, DisplayStatus, MoneyValue } from "./models";

export interface VNextScopeProps { children: ReactNode; theme?: "light" | "dark"; className?: string }
export function VNextScope({ children, theme, className = "" }: VNextScopeProps) {
  return <div className={`dt-vnext ${className}`} data-theme={theme}>{children}</div>;
}

export interface MoneyAmountProps extends MoneyValue { kind?: "amount" | "limit"; size?: "body" | "display"; invalidLabel?: string }
/** `precision` (from the MoneyValue) applies to amounts; a limit is always whole dollars. */
export function MoneyAmount({ amount, currency, precision, kind = "amount", size = "body", invalidLabel = "Amount unavailable" }: MoneyAmountProps) {
  const formatted = formatMoney(amount, kind, kind === "limit" ? 0 : precision);
  return <span className={`dt-money dt-money--${size}`} data-invalid={formatted === null || undefined} aria-label={formatted === null ? invalidLabel : `${formatted} ${currency}`}>
    {formatted ?? "—"}{formatted !== null && <span className="dt-money-unit">{currency}</span>}
  </span>;
}

export function TokenQuantity({ amount, currency, precision }: MoneyValue & { precision: number }) {
  const formatted = formatTokenQuantity(amount, precision);
  return <span className="dt-money" aria-label={formatted === null ? "Quantity unavailable" : `${formatted} ${currency}`}>{formatted ?? "—"}{formatted !== null && <span className="dt-money-unit">{currency}</span>}</span>;
}

export interface VNextButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  variant?: "primary" | "secondary" | "quiet";
  busy?: boolean;
  busyLabel?: string;
  onClick?: () => void | Promise<void>;
  errorLabel?: string;
}
export function VNextButton({ variant = "primary", busy = false, busyLabel = "Working", errorLabel = "Action could not be completed. Check its result before trying again.", children, className = "", onClick, disabled, type = "button", ...props }: VNextButtonProps) {
  const locked = useRef(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const isBusy = busy || pending;
  const handleClick = async () => {
    if (locked.current || isBusy || disabled) return;
    locked.current = true;
    setFailed(false);
    try {
      const result = onClick?.();
      if (result instanceof Promise) { setPending(true); await result; }
    } catch { setFailed(true); } finally { locked.current = false; setPending(false); }
  };
  return <><button {...props} type={type} className={`dt-button dt-button--${variant} ${className}`} disabled={disabled || isBusy || failed} aria-busy={isBusy} onClick={handleClick}>
    {isBusy ? <><LoaderCircle className="dt-spinner" aria-hidden="true" />{busyLabel}</> : children}
  </button>{failed && <span role="alert" className="dt-error-text">{errorLabel}</span>}</>;
}

export interface AmountFieldProps { label: string; value: string; currency: string; onChange: (value: string) => void; error?: string; disabled?: boolean; name?: string }
export function AmountField({ label, value, currency, onChange, error, disabled, name }: AmountFieldProps) {
  const id = useId();
  return <div className="dt-field"><label htmlFor={id}>{label}</label><div className="dt-amount-input"><input id={id} name={name} inputMode="decimal" autoComplete="off" value={value} onChange={event => onChange(event.target.value)} disabled={disabled} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} /><span>{currency}</span></div>{error && <p id={`${id}-error`} role="alert" className="dt-error-text">{error}</p>}</div>;
}

export type IdentityKind = "person" | "group" | "agent" | "fleet";
export interface IdentityAvatarProps { name: string; kind: IdentityKind; initials?: string; small?: boolean }
export function IdentityAvatar({ name, kind, initials, small }: IdentityAvatarProps) {
  const Icon = kind === "agent" ? Bot : kind === "fleet" ? ArrowUpRight : Users;
  return <span className={`dt-avatar dt-avatar--${kind}${small ? " dt-avatar--small" : ""}`} role="img" aria-label={`${name}, ${kind}`}>{kind === "person" ? initials ?? Array.from(name)[0] : <Icon aria-hidden="true" />}</span>;
}

const STATUS_LABELS: Record<DisplayStatus, string> = {
  draft: "Draft", "approval-required": "Needs you", submitting: "Submitting", settled: "Settled", failed: "Failed", unknown: "Result unknown", paused: "Paused", partial: "Partially complete", active: "Active", "waiting-for-input": "Waiting for input",
};
export interface StatusPillProps { status: DisplayStatus; label?: string }
export function StatusPill({ status, label }: StatusPillProps) {
  return <span className={`dt-status dt-status--${status}`}>{status === "settled" && <Check aria-hidden="true" />}{label ?? STATUS_LABELS[status]}</span>;
}

export interface AuthorityCardProps { authority: AuthorityView; children: ReactNode; className?: string; executionStatus?: DisplayStatus }
export function AuthorityCard({ authority, children, className = "", executionStatus }: AuthorityCardProps) {
  const waiting = (executionStatus === undefined || executionStatus === "approval-required") && authority.tier === "T3" && authority.state === "approval-required";
  return <section className={`dt-authority-card ${className}`} data-authority={authority.tier} data-authority-state={authority.state} data-needs-you={waiting}>
    <div className="dt-authority-label"><span>{authority.tier}</span>{!waiting && authority.state === "approval-required" && executionStatus !== undefined ? "Operation authority record" : authority.label}</div>{authority.detail && <p className="dt-caption">{authority.detail}</p>}{children}
  </section>;
}

export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return <div className="dt-detail-row"><span>{label}</span><span>{children}</span></div>;
}

/**
 * An identity or count chip ("AGENT", "FLEET", "3 people", "Agent wallet").
 * It carries no status meaning: never amber, never a live state — that is
 * `StatusPill`'s job and only its job.
 */
export interface MetaChipProps { children: ReactNode; tone?: "neutral" | "blue" }
export function MetaChip({ children, tone = "neutral" }: MetaChipProps) {
  return <span className={`dt-meta-chip dt-meta-chip--${tone}`}>{children}</span>;
}

/** A page's title row with an optional back control and trailing actions. */
export interface PageHeaderProps { title: string; subtitle?: string; onBack?: () => void; backLabel?: string; actions?: ReactNode }
export function PageHeader({ title, subtitle, onBack, backLabel = "Back", actions }: PageHeaderProps) {
  return <header className="dt-page-header">
    {onBack && <button type="button" className="dt-button dt-button--quiet dt-page-header-back" onClick={onBack}><ArrowLeft aria-hidden="true" />{backLabel}</button>}
    <div className="dt-page-header-copy"><h1>{title}</h1>{subtitle && <p className="dt-caption">{subtitle}</p>}</div>
    {actions && <div className="dt-page-header-actions">{actions}</div>}
  </header>;
}

export function ReceiptDisclosure({ summary = "View receipt", children }: { summary?: string; children: ReactNode }) {
  return <details className="dt-disclosure"><summary>{summary}<ChevronRight aria-hidden="true" /></summary><div>{children}</div></details>;
}
